<template>
  <div class="note-category-paginated-overview" v-bind:class="{loaded: !loading}">
    <template v-if="showFilterOptions">
      <overview-filter-component
          v-if="hasItems || filterActive"
          v-bind:customer-id="customerId"
          v-on:filter-request="getFilteredAttachables($event)"
      />
      <Skeleton v-else-if="loading" style="height: 85px; width: 100%;"></Skeleton>
    </template>

    <div v-if="!loading || hasItems || filterActive">
      <laravel-pagination
          v-if="displayPagination"
          v-bind:data="currentRequestData"
          v-on:pagination-change-page="loadPage"
          class="pagination-buttons"
      />

      <div v-if="hasItems || filterActive" class="per-page"
           style="display: inline-block; vertical-align: text-top; margin: 0; padding: 8px;">
        Per pagina
        <a-select v-model="perPage" v-on:change="onPerPageChange" style="width: 70px;">
          <a-select-option v-bind:value="10">10</a-select-option>
          <a-select-option v-bind:value="15">15</a-select-option>
        </a-select>
      </div>
    </div>
    <div v-else style="padding: 8px;">
      <Skeleton style="width: 66px; height: 19px; display: inline-block; vertical-align: middle;"></Skeleton>
      <Skeleton style="width: 70px; height: 32px; display: inline-block; vertical-align: middle;"></Skeleton>
    </div>

    <div v-if="!hasItems && !loading" style="padding: 8px;">
      <p v-if="filterActive">
        Er zijn geen notities die voldoen aan de huidige filtering.
      </p>
      <p v-else-if="error">
        Er ging iets mis. Het is niet gelukt de notities te laden.
      </p>
      <p v-else>
        Er zijn nog geen notities voor deze categorie.
      </p>
    </div>

    <table v-else class="table table-striped">
      <thead>
      <tr>
        <th style="vertical-align: text-top">
          <order-by value="child_name" v-bind:order="order" v-bind:select="orderBy" v-on:change="onOrderByChange">
            Onderwerp
          </order-by>
        </th>
        <th class="compact" style="vertical-align: text-top">
          <order-by value="parent_id" v-bind:order="order" v-bind:select="orderBy" v-on:change="onOrderByChange">
            Gekoppeld aan
          </order-by>
        </th>
        <th class="compact" style="vertical-align: text-top">
          <order-by value="created_at" v-bind:order="order" v-bind:select="orderBy" v-on:change="onOrderByChange">
            Aangemaakt op
          </order-by>
        </th>
        <th class="compact" style="vertical-align: text-top">
          <order-by value="user" v-bind:order="order" v-bind:select="orderBy" v-on:change="onOrderByChange">
            Door
          </order-by>
        </th>
        <th class="compact" style="vertical-align: text-top">
          <order-by value="visible_for_customer" v-bind:order="order" v-bind:select="orderBy"
                    v-on:change="onOrderByChange">
            <eye-icon></eye-icon>
          </order-by>
        </th>
        <th class="compact" title="Actie knoppen">

        </th>
      </tr>
      </thead>
      <tbody>
      <template v-if="loading">
        <tr v-for="row in loadingCount" v-bind:key="'skeleton-row' + row" class="attachable-note-row">
          <td>
            <Skeleton class="skeleton-paragraph"/>
          </td>
          <td>
            <Skeleton class="skeleton-paragraph"/>
          </td>
          <td>
            <Skeleton class="skeleton-paragraph"/>
          </td>
          <td>
            <Skeleton class="skeleton-user-badge"/>
          </td>
          <td class="center">
            <Skeleton class="skeleton-icon"/>
          </td>
          <td class="text-right">
            <Skeleton class="skeleton-button"/>
          </td>
        </tr>
      </template>

      <attachable-note-table-row-component
          v-else
          v-for="attachable in attachables"
          v-on:note-updated='handleNoteUpdated(attachable.id)'
          v-on:delete='onDelete'
          v-bind:key="attachable.id"
          v-bind:attachable="attachable"
          v-bind:publish-documents="publishDocuments"
      />
      </tbody>
    </table>

    <laravel-pagination
        v-if="displayPagination"
        v-bind:data="currentRequestData"
        v-on:pagination-change-page="loadPage"
        class="pagination-buttons"
    />
  </div>
</template>

<script>
import attachableApi from '~/api/attachable'
import AttachableNoteTableRowComponent from './AttachableRowComponent'
import OverviewFilterComponent from './OverviewFilterComponent'
import { Skeleton } from 'vue-loading-skeleton'

export default {
  name: 'NoteCategoryPaginatedOverviewComponent',
  props: {
    categoryId: {
      type: [Number, String],
      required: true,
    },
    customerId: {
      type: [Number, String],
      default: null,
    },
    instance: {
      type: Object,
      default: null
    },
    publishDocuments: {
      type: Boolean,
      default: false,
    },
    showFilterOptions: {
      type: Boolean,
      default: true,
    },
    count: {
      type: Number,
      default: 2,
    }
  },
  components: {
    AttachableNoteTableRowComponent,
    OverviewFilterComponent,
    Skeleton,
  },
  data () {
    return {
      reloadTimeout: null,
      attachables: [],
      currentPage: null,
      currentRequestData: null,
      loading: true,
      error: false,
      page: 1,
      perPage: 15,
      orderBy: 'created_at',
      order: 'desc',
      filter: {},
      context: null,
      childClass: 'App\\Models\\Note',
      includes: ['child_instance', 'parent_instance', 'owner', 'child_instance.product_labels'],
    }
  },
  created () {
    this.setContext()
    this.loadPage(this.page)
  },
  computed: {
    loadingCount () {
      return this.count < this.perPage ? this.count : this.perPage
    },
    displayPagination () {
      return this.loading === false && this.hasItems && typeof this.currentRequestData.meta !== 'undefined'
    },
    hasItems () {
      return this.currentTotalItemCount !== null && this.currentTotalItemCount > 0
    },
    currentTotalItemCount () {
      return (
          this.currentRequestData !== null
          && typeof this.currentRequestData.meta !== 'undefined'
          && typeof this.currentRequestData.meta !== null
          && typeof this.currentRequestData.meta.pagination !== 'undefined'
          && typeof this.currentRequestData.meta.pagination !== null
          && typeof this.currentRequestData.meta.pagination.total !== 'undefined'
          && typeof this.currentRequestData.meta.pagination.total !== null
      )
          ? this.currentRequestData.meta.pagination.total
          : null
    },
    filterActive () {
      if (Object.entries(this.filter)) {
        for (const [key, value] of Object.entries(this.filter)) {
          if (value !== null && value.length > 0) return true
        }
      }
      return false
    }
  },
  methods: {
    setContext () {
      if (this.instance !== null) {
        this.context = 'instance'
      } else {
        this.context = 'family'
      }
    },
    loadPage (page = 1, forceReload = false) {
      if (page === this.currentPage && forceReload !== true) {
        return
      }
      this.loading = true

      if (this.context === 'instance') {
        attachableApi.paginateByInstanceAndChildClass(
            this.instance,
            this.childClass,
            page,
            this.perPage,
            this.orderBy,
            this.order,
            this.includes,
            this.filter
        ).then((response) => {
          this.attachables = response.data
          this.currentRequestData = response
          this.currentPage = page
        }).catch((error) => {
          this.error = true

          this.$notification['error']({
            message: 'Er ging iets mis',
            description: 'Het is niet gelukt de notities te laden.',
          })
        }).finally(() => {
          this.loading = false
        })
      } else {
        attachableApi.paginateByFamilyCategoryAndChildClass(
            this.customerId,
            this.categoryId,
            this.childClass,
            page,
            this.perPage,
            this.orderBy,
            this.order,
            this.includes,
            this.filter
        ).then((response) => {
          this.attachables = response.data
          this.currentRequestData = response
          this.loading = false
          this.currentPage = page
        }).catch(() => {
          this.error = true

          this.$notification['error']({
            message: 'Er ging iets mis',
            description: 'Het is niet gelukt de notities te laden.',
          })
        }).finally(() => {
          this.loading = false
        })
      }
    },
    onPerPageChange (value) {
      if (this.currentTotalItemCount !== null) {
        let newTotalPages = Math.ceil(this.currentTotalItemCount / value)

        if (this.currentPage > newTotalPages) {
          this.page = 1
        } else {
          this.page = this.currentPage
        }
      } else {
        this.page = 1
      }

      this.reloadPage()
    },
    onOrderByChange (orderBy, order) {
      this.orderBy = orderBy
      this.order = order
      this.loadPage(this.currentPage, true)
    },
    reloadPage (page = null) {
      if (page === null) {
        this.page = this.currentPage
      } else {
        this.page = page
      }

      if (this.reloadTimeout !== null) {
        clearTimeout(this.reloadTimeout)
      }

      this.reloadTimeout = setTimeout(() => {
        this.loadPage(this.page, true)
      }, 200)
    },
    getFilteredAttachables (params) {
      this.filter = params
      this.loadPage(this.page, true)
    },
    handleNoteUpdated (attachableId) {
      attachableApi.find(attachableId, this.includes)
          .then((response) => {
            let index = this.attachables.findIndex((attachable) => attachable.id === response.data.id)
            this.$set(this.attachables, index, response.data)
          })
    },
    onDelete (attachable) {
      let $self = this

      this.$confirm({
        title: 'Weet je zeker dat je deze notitie wil verwijderen?',
        content: 'De geselecteerde notitie zal worden verwijderd. Deze actie is niet terug te draaien',
        okText: 'Verwijderen',
        okType: 'danger',
        cancelText: 'Annuleren',
        onOk () {
          attachableApi.delete(attachable.id)
              .then(() => {
                this.$notification['success']({
                  message: 'Success',
                  description: 'De notitie is verwijderd',
                })
              })
              .catch(() => {
                this.$notification['error']({
                  message: 'Er ging iets mis',
                  description: 'Het is niet gelukt de notitie te verwijderen',
                })
              })
              .finally(async () => {
                await $self.reloadPage()
                this.loading = false
              })
        },
        onCancel () {
        },
      })
    },
  },
}
</script>

<style scoped>
.note-category-paginated-overview {
  min-height: 50px;
}

table th.compact, table td.compact {
  white-space: nowrap;
  width: 1px !important;
}

table > tbody.loading {
  opacity: 0.5;
}

table {
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.pagination-buttons {
  display: inline-block;
  vertical-align: text-top;
  margin: 0;
  padding: 8px;
}

.skeleton-paragraph {
  width: 100%;
  height: 1.5em;
  display: inline-block;
}

.skeleton-user-badge {
  width: 93px;
  height: 32px;
  display: inline-block;
}

.skeleton-icon {
  width: 30px;
  height: 23px;
  display: inline-block;
  border-radius: 4px;
}

.skeleton-button {
  width: 46px;
  height: 32px;
  display: inline-block;
  border-radius: 4px;
}
</style>
